/**
 * executiveChartModel — the pure model behind the /admin executive charts (card 3871).
 *
 * WHY THIS IS NOT INSIDE ExecutiveCharts.jsx: jest on this tree runs with `transform: {}`, has
 * no babel config at all, and `moduleFileExtensions: ['js','mjs','json']` — a `.jsx` file is not
 * even a resolvable module here, so a helper that lives in the component CANNOT be unit-tested.
 * `web/utils/fovReconciliation.js` moved out for exactly this reason and says so in its header.
 * Pure logic lives here; the component stays the effect boundary.
 *
 * ⛔ NOTHING HERE COMPUTES MONEY. `buildTimeseries`
 *   (api/services/analytics/executiveMetricsService.js) already carries gross / net / expense /
 *   profit per bucket for BOTH populations. The one subtraction below — reserve = gross − net —
 *   is not a second opinion about profit: the server DEFINES `netRevenueCents = gross - reserve`,
 *   so that gap IS the stored reserve read back. It exists only because the per-bucket
 *   `firstOrder` block publishes no reserve field of its own, and criterion 1 requires the
 *   gross → reserve → net → expense → profit chain to stay readable in the tooltip.
 *
 * ⛔ NO POPULATION OR METRIC NAME IS WRITTEN IN THIS FILE. Every label the captions render
 *   arrives as the API's own `population` block, so the UI and the endpoint cannot drift into
 *   two different names for one population.
 */

/**
 * Pure. `null` for anything that is not a finite number, INCLUDING `null`, `undefined` and `''` —
 * all of which `Number()` would happily turn into `0`. Deliberately a byte-for-byte copy of
 * `ExecutiveKpiRow.toFiniteOrNull` rather than an import: importing it would drag a `.jsx` module
 * into this file and re-break the very testability this file exists to provide.
 */
export const toFiniteOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * The six periods the ONE control offers, in render order. Keys are the exact key space of
 * `GET /admin/analytics/executive` (`PERIOD_KEYS` in executiveMetricsService.js) — an unknown
 * key answers 400, not a silent default, so an invented key here would blank the whole page.
 *
 * 🪤 `last_month` is the card's "prior months". The endpoint has exactly one prior-calendar
 *   window, so the label says what it actually measures rather than implying several.
 */
export const EXECUTIVE_PERIODS = Object.freeze([
  Object.freeze({ key: 'this_week', label: 'This Week' }),
  Object.freeze({ key: 'last_week', label: 'Last Week' }),
  Object.freeze({ key: 'this_month', label: 'This Month' }),
  Object.freeze({ key: 'last_month', label: 'Prior Month' }),
  Object.freeze({ key: 'last_3_months', label: 'Last 3 Months' }),
  Object.freeze({ key: 'quarter', label: 'Quarter' }),
  Object.freeze({ key: 'all_time', label: 'All Time' })
]);

/**
 * Every fresh admin visit opens on the trailing three-month view. This is intentionally a
 * product default, not persisted browser state; the period control can still change it afterward.
 *
 * ⚠️ THIS KEY MUST EXIST IN THE SERVER'S `PERIOD_KEYS` (card 4309). An unknown period answers
 * **400**, and the executive block then renders blank with zero console errors.
 */
export const DEFAULT_EXECUTIVE_PERIOD = 'last_3_months';

export const isOfferedPeriod = (key) => EXECUTIVE_PERIODS.some((p) => p.key === key);

/**
 * 🪤 THE ARRIVAL DEFAULT MUST EXIST IN THE OFFERED KEY SPACE. A selector whose current key is
 * not among its options renders nothing selected and every chart empty, with zero console
 * errors — the exact failure card 2255's criterion 4 names. Routing every read through this
 * guard makes that unrepresentable instead of merely untested.
 */
export const resolveOfferedPeriod = (key) => (isOfferedPeriod(key) ? key : DEFAULT_EXECUTIVE_PERIOD);

/**
 * Pure. A KPI tile label carrying the ACTIVE period in parentheses (card 5135).
 *
 * 🔴 THIS IS A RENDER-TIME DERIVATION AND MUST STAY ONE. `EXECUTIVE_KPI_DEFS[].label` is read by
 *    THREE other consumers that must NOT gain a period suffix — `deriveSummaryTiles`
 *    (web/utils/executiveSummaryTiles.js, the summary band), the `tileLabels` feeding
 *    `formatOtherPopulationNotice` (web/utils/executiveScope.js, the boundary sentence), and
 *    `MARGIN_DEF.label` in ExecutiveCharts.jsx. All three read `.label` off the SAME frozen defs,
 *    so mutating that string would change four surfaces from one edit. Suffixing at render
 *    changes exactly the one surface that asked for it.
 *
 * An unknown or absent key returns the BARE label — ⛔ never `(undefined)` and ⛔ never `()`.
 * A key the selector cannot offer names no window, so printing it is worse than saying nothing.
 */
/**
 * The five executive tiles whose labels carry the ACTIVE period (card 5135).
 *
 * Every displayed headline is period-scoped. Profit Margin names the active window too, so a
 * Last 3 Months selection cannot be mistaken for an all-time ratio.
 *
 * 🔑 This lives here, not as a flag on `EXECUTIVE_KPI_DEFS`, for two reasons: the defs stay
 *    BYTE-UNTOUCHED (three other consumers read `.label` off them), and jest on this tree cannot
 *    import a `.jsx`, so a flag over there could only ever be asserted as source TEXT while this
 *    is testable by EXECUTION.
 */
export const PERIOD_SCOPED_KPI_KEYS = Object.freeze([
  'totalMembers',
  'newMembers',
  'grossRevenue',
  'totalExpense',
  'profit',
  'profitMargin'
]);

/** Pure predicate. Does this tile's label name the active period? */
export const isPeriodScopedKpi = (key) => PERIOD_SCOPED_KPI_KEYS.includes(key);

export const formatKpiLabelForPeriod = (label, periodKey) => {
  if (typeof label !== 'string' || label.trim() === '') return label;
  const period = EXECUTIVE_PERIODS.find((p) => p.key === periodKey) ?? null;
  return period === null ? label : `${label} (${period.label})`;
};

/**
 * Pure. WHICH period key the tile labels must name: the one the SERVER echoed alongside the
 * figures that are on screen right now.
 *
 * 🪤 DERIVING THE SUFFIX FROM THE SELECTED KEY IS A PLAUSIBLE-LOOKING LIE. Card 5133 deliberately
 *    keeps the tiles MOUNTED across a period change, so between the click and the response the
 *    numbers on screen still belong to the PREVIOUS window. A label driven by the selection would
 *    print `(Last Week)` above All Time figures for the whole in-flight window — a confident,
 *    wrong business claim, and exactly the class of defect the em-dash rule already guards.
 *    `metrics.period` travelled with those numbers and therefore cannot race them.
 *
 * The selected key is the fallback ONLY when there are no figures at all yet (`metrics` nullish),
 * where there is nothing for it to contradict.
 */
export const resolveKpiPeriodKey = (metrics, selectedKey) =>
  (metrics === null || metrics === undefined ? selectedKey : metrics.period);

/**
 * The grain control's key space — the exact `GRANULARITY_KEYS` of the endpoint. The controller
 * answers **400** on an unknown grain (deliberately: card 3868 refused a silent fall-back,
 * because a typo that quietly returned month buckets would render a plausible chart of the
 * wrong thing). That 400 is why `resolveOfferedGranularity` below is load-bearing and not
 * decoration — it is the same guard, for the same failure, as `resolveOfferedPeriod`.
 *
 * ⛔ THE GRAIN IS NOT THE WINDOW. These three are orthogonal to EXECUTIVE_PERIODS: the period
 *   says which window, the grain says how finely to cut it. Merging them would delete a
 *   capability, not simplify one.
 */
export const EXECUTIVE_GRANULARITIES = Object.freeze([
  Object.freeze({ key: 'day', label: 'Daily' }),
  Object.freeze({ key: 'week', label: 'Weekly' }),
  Object.freeze({ key: 'month', label: 'Monthly' })
]);

/**
 * 🪤 A MONTHLY GRAIN OVER A MONTHLY-ISH WINDOW DRAWS ONE POINT, AND ONE POINT IS NOT A
 *   TIMELINE (card 4310). Measured before the change: `this_month&granularity=month` returned
 *   `timeseries.length = 1` — a chart with a single dot and no trend, on first load, with
 *   nothing on screen saying anything was wrong. Weekly over three months is ~13 points.
 */
export const DEFAULT_EXECUTIVE_GRANULARITY = 'week';

export const isOfferedGranularity = (key) => EXECUTIVE_GRANULARITIES.some((g) => g.key === key);

export const resolveOfferedGranularity = (key) =>
  (isOfferedGranularity(key) ? key : DEFAULT_EXECUTIVE_GRANULARITY);

/**
 * The two views the ONE headline card switches between. Purely a client-side concern — the view
 * never reaches the API, because both views are drawn from the SAME response. That is what makes
 * the money card and the members card unable to disagree about a period or a grain.
 */
export const EXECUTIVE_VIEWS = Object.freeze([
  Object.freeze({ key: 'money', label: 'Revenue & Profit' }),
  Object.freeze({ key: 'members', label: 'Members' })
]);

export const DEFAULT_EXECUTIVE_VIEW = 'money';

export const isOfferedView = (key) => EXECUTIVE_VIEWS.some((v) => v.key === key);

export const resolveOfferedView = (key) => (isOfferedView(key) ? key : DEFAULT_EXECUTIVE_VIEW);

/**
 * The money view's DOLLAR line series — EXACTLY THREE, all on the left-hand money axis
 * (card 4310 promotes `totalExpense` from tooltip-only to drawn: revenue, costs and profit are
 * the three the brief names, and a cost line is what makes a negative profit legible instead of
 * mysterious). Net revenue and reserve withheld stay in TOOLTIP_LINES — ⛔ they are NOT deleted;
 * five drawn lines is an unreadable chart, and dropping them from the tooltip would buy
 * legibility with accounting truth.
 */
export const MONEY_SERIES = Object.freeze([
  Object.freeze({ dataKey: 'grossRevenue', name: 'Gross Revenue', stroke: '#2d2d2d', yAxisId: 'money' }),
  Object.freeze({ dataKey: 'totalExpense', name: 'Costs', stroke: '#f59e0b', yAxisId: 'money' }),
  Object.freeze({ dataKey: 'profit', name: 'Profit', stroke: '#22c55e', yAxisId: 'money' })
]);

/**
 * User growth, drawn beside the money on its OWN right-hand axis.
 *
 * 🔑 THE SECOND AXIS IS LOAD-BEARING, ⛔ NOT DECORATION. `newMembers` is a COUNT (single/double
 *   digits per week) and the money lines are DOLLARS (thousands). Sharing one axis does not make
 *   them comparable — it flattens the member series onto the zero line and makes it unreadable,
 *   while the reader has no way to tell that from a genuine zero. Two axes, two units.
 */
export const MEMBERS_SERIES = Object.freeze({
  dataKey: 'newMembers', name: 'New Members', stroke: '#3b82f6', yAxisId: 'members'
});

/**
 * EVERY line the money chart draws, in render order. The component maps over THIS list, so the
 * `recharts-line` count in the DOM is a FUNCTION of it rather than a second hand-maintained copy
 * — asserting `MONEY_SERIES.length === 3` and asserting the DOM count therefore cannot disagree,
 * and a stale bundle cannot pass by satisfying only one of them.
 */
export const MONEY_CHART_SERIES = Object.freeze([...MONEY_SERIES, MEMBERS_SERIES]);

/**
 * CARD 5338 — the money chart's axis configuration for a phone, as ONE PURE FUNCTION of ONE boolean.
 *
 * It lives here and not in `ExecutiveCharts.jsx` for the reason stated at the top of that file: jest
 * on this tree cannot import a `.jsx` (no transform, `jsx` not in `moduleFileExtensions`), so an
 * inline ternary in the component would be untestable BY EXECUTION and could only ever be pinned as
 * source text. The precedents are `resolveExecutivePanelState` and `deriveRevenueIntegrity`.
 *
 * 🔑 THE POINT IS THAT LESS INK IS DRAWN, ⛔ NOT THAT LESS DATA EXISTS. Every series, both axes and
 *   the whole tooltip chain survive at every width — see `MONEY_CHART_SERIES` (four lines on two
 *   axes) and `TOOLTIP_LINES` (the five-row gross → reserve → net → expense → profit chain), neither
 *   of which this function can reach. What narrows is only the axis CHROME: how many date labels the
 *   x-axis draws, how much width the two y-axes reserve, and whether the right-hand count axis
 *   renders its tick labels at all.
 *
 * 🪤 `xMinTickGap` MOVES THE OPPOSITE WAY FROM THE WIDTHS, AND THAT IS THE WHOLE MECHANISM.
 *   `minTickGap` is recharts' MINIMUM PIXEL GAP BETWEEN TICKS, so it must be RAISED to draw FEWER
 *   labels. A reader who assumes "phone ⇒ every number gets smaller" will lower it and silently get
 *   MORE overlapping date labels on the narrowest screen — the exact defect this card exists to fix.
 *   The test asserts each field with its own correct direction for precisely this reason.
 */
export const WIDE_CHART_AXIS = Object.freeze({
  xMinTickGap: 16,
  moneyAxisWidth: 66,
  moneyTickFormat: 'full',
  membersAxisWidth: 40,
  membersAxisTick: true,
  legendFontSize: 0.7
});

export const NARROW_CHART_AXIS = Object.freeze({
  xMinTickGap: 44,
  moneyAxisWidth: 40,
  moneyTickFormat: 'compact',
  membersAxisWidth: 12,
  membersAxisTick: false,
  legendFontSize: 0.625
});

/** Pure, total, and referentially transparent: one boolean in, one frozen config out. */
export const deriveChartAxisConfig = (isNarrow) => (isNarrow ? NARROW_CHART_AXIS : WIDE_CHART_AXIS);

/**
 * Compact dollar ticks for the narrow money axis — `$0` · `$940` · `$1.2k` · `$14k` · `$1.3M`.
 *
 * A 66px axis rendering `$12,345` does not fit in the ~40px a 390px screen can spare, and recharts
 * does not truncate: it overlaps the label into the plot. This shortens the LABEL, ⛔ never the
 * value — the tooltip still renders full precision through `TOOLTIP_LINES`.
 */
export const formatCompactMoney = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '$0';
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  if (abs >= 1_000_000) {
    const m = abs / 1_000_000;
    return `${sign}$${m >= 10 ? Math.round(m) : Math.round(m * 10) / 10}M`;
  }
  if (abs >= 1_000) {
    const k = abs / 1_000;
    return `${sign}$${k >= 10 ? Math.round(k) : Math.round(k * 10) / 10}k`;
  }
  return `${sign}$${Math.round(abs)}`;
};

/** The chain, in accounting order, that the tooltip keeps readable: gross → reserve → net → expense → profit. */
export const TOOLTIP_LINES = Object.freeze([
  Object.freeze({ dataKey: 'grossRevenue', name: 'Gross Revenue' }),
  Object.freeze({ dataKey: 'reserveWithheld', name: 'Reserve Withheld' }),
  Object.freeze({ dataKey: 'netRevenue', name: 'Net Revenue' }),
  Object.freeze({ dataKey: 'totalExpense', name: 'Total Expense' }),
  Object.freeze({ dataKey: 'profit', name: 'Profit' })
]);

const MONTH_NAMES = Object.freeze([
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
]);

/**
 * Pure. Bucket key → axis label.
 *
 * 🪤 A WEEK KEY AND A DAY KEY ARE THE SAME STRING. `bucketKeyFor` emits the week's start date, so
 *   both grains produce `YYYY-MM-DD` and reading the key's LENGTH cannot tell them apart — a
 *   week of revenue would silently render as a single day. The grain is therefore an INPUT here,
 *   read from the payload's echoed `granularity`, ⛔ never inferred from the key.
 *
 *     day    '2026-08-03' → 'Aug 3'
 *     week   '2026-08-03' → 'Wk of Aug 3'      ← same key, different label
 *     month  '2026-08'    → 'Aug 2026'
 */
export const formatBucketLabel = (bucket, granularity) => {
  const key = String(bucket ?? '');
  const [y, m, d] = key.split('-');
  const monthIndex = Number(m) - 1;
  const name = Number.isInteger(monthIndex) && monthIndex >= 0 && monthIndex <= 11
    ? MONTH_NAMES[monthIndex]
    : key;
  if (!d) return `${name} ${y}`;
  return granularity === 'week' ? `Wk of ${name} ${Number(d)}` : `${name} ${Number(d)}`;
};

/** The mark an incomplete bucket carries on the axis. Punctuation, not copy; no emojis (STANDARDS). */
export const PARTIAL_BUCKET_MARKER = '*';

const MS_PER_DAY = 86400000;

/**
 * Pure. The half-open span `[start, end)` in epoch ms that a bucket key covers at a given grain,
 * or `null` when the key is unreadable.
 *
 * 🪤 THE MONTH BRANCH IS SELECTED BY THE KEY'S SHAPE, NOT BY THE GRAIN, for the same reason
 *   `formatBucketLabel` does it: `bucketKeyFor` emits `YYYY-MM` for month and `YYYY-MM-DD` for
 *   BOTH day and week, so a three-part key is never a month.
 *
 * 🪤 EVERY FIELD IS `Number.isFinite`-GUARDED BEFORE IT REACHES A COMPARISON. `Number(undefined)`
 *   is `NaN`, and `NaN < x` is `false` — so an unparsed key would not throw and would not warn,
 *   it would silently report every bucket COMPLETE, which is exactly the reassuring direction.
 */
export const deriveBucketSpanMs = (bucket, granularity) => {
  const parts = String(bucket ?? '').split('-');
  const year = Number(parts[0]);
  const month = Number(parts[1]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || parts.length < 2) return null;

  if (parts.length < 3) {
    return { start: Date.UTC(year, month - 1, 1), end: Date.UTC(year, month, 1) };
  }

  const day = Number(parts[2]);
  if (!Number.isFinite(day)) return null;
  const start = Date.UTC(year, month - 1, day);
  return { start, end: start + (granularity === 'week' ? 7 : 1) * MS_PER_DAY };
};

/**
 * Pure. A bucket is PARTIAL when the drawn window does not cover its whole span.
 *
 * 🔑 WHY THIS EXISTS: the server buckets by CALENDAR unit (`startOfUtcWeek`, Sunday-anchored)
 *   while the window starts wherever the period says. At `quarter&granularity=week` the first
 *   bucket is `2026-06-28` for a window opening `2026-07-01` — four of its seven days are
 *   outside the window, so it renders as a real dip that no one can distinguish from a bad
 *   trading week. ⛔ Dropping it would hide real money; LABELLING it is the honest repair.
 *
 * ⚠️ The TRAILING bucket is normally partial too — a rolling window ends at `now`, mid-week.
 *   That is not a bug in this predicate: the current week genuinely is incomplete, and the same
 *   mark says so at both ends.
 */
export const isPartialBucket = (bucket, granularity, window) => {
  const span = deriveBucketSpanMs(bucket, granularity);
  if (span === null) return false;
  const windowStart = Date.parse(window?.start ?? '');
  const windowEnd = Date.parse(window?.end ?? '');
  if (!Number.isFinite(windowStart) || !Number.isFinite(windowEnd)) return false;
  return span.start < windowStart || span.end > windowEnd;
};

/** Pure. How many drawn buckets are incomplete — published to the DOM so a test needs no OCR. */
export const derivePartialCount = (points = []) =>
  (Array.isArray(points) ? points : []).filter((p) => p?.isPartial === true).length;

/**
 * Pure. The reserve a bucket withheld, in integer CENTS, read back out of two figures the
 * payload already publishes. ⛔ Not a re-derivation of profit: `buildTimeseries` defines
 * `netRevenueCents = grossCents - reserveCents`, so `gross - net` IS the stored reserve. Done in
 * cents (which the payload also carries) so the readback is exact and cannot drift by a float
 * epsilon. `null` when either side is unknown — an unknown reserve must render EMPTY_VALUE.
 */
export const deriveReserveWithheldCents = (grossCents, netCents) => {
  const g = toFiniteOrNull(grossCents);
  const n = toFiniteOrNull(netCents);
  return g === null || n === null ? null : g - n;
};

/** Pure. Integer cents → dollars, preserving "unknown" as `null` rather than collapsing it to 0. */
export const convertCentsToDollars = (cents) => {
  const c = toFiniteOrNull(cents);
  return c === null ? null : c / 100;
};

/**
 * Pure. One drawable point per bucket: the running member total, an axis label, and the money
 * chain for the population the money view draws.
 *
 * 🔑 TOTAL MEMBERS IS A CUMULATIVE DERIVED FROM AN OPENING BALANCE, NOT A STORED SERIES.
 *   `totalMembers` is the count as of NOW and `newMembers` is per bucket, so the balance at the
 *   START of the window is `totalMembers - Σ newMembers`. Running that forward lands exactly on
 *   `totalMembers` at the last bucket. Seeding at 0 instead would draw a truthful-looking curve
 *   that ends at 705 members on a platform with 4,151.
 *
 * ⚠️ THE MONEY KEYS ARE THE **FIRST-ORDER** POPULATION (criterion 1), which is why they override
 *   the all-orders values of the same name from the spread. When the payload carries no
 *   `firstOrder` block (an endpoint older than card 3868) they resolve to `null` and the chart
 *   renders EMPTY_VALUE. ⛔ It deliberately does NOT fall back to the all-orders figures: that
 *   would draw one population under a caption naming another, which is the precise drift this
 *   card's server-supplied labels exist to prevent. The all-orders integers survive untouched on
 *   the spread as `grossRevenueCents` / `netRevenueCents` / `profitCents`.
 */
export const deriveChartPoints = (
  timeseries = [], totalMembers = 0, granularity = undefined, window = null
) => {
  const points = Array.isArray(timeseries) ? timeseries : [];
  const newInWindow = points.reduce((sum, p) => sum + (Number(p?.newMembers) || 0), 0);
  const opening = (Number(totalMembers) || 0) - newInWindow;

  let running = opening;
  return points.map((p) => {
    running += Number(p?.newMembers) || 0;
    const fo = p?.firstOrder ?? {};
    const isPartial = isPartialBucket(p?.bucket, granularity, window);
    return {
      ...p,
      /* The mark rides on the AXIS LABEL rather than on `formatBucketLabel`, which stays a pure
         key→label function with no notion of a window. Absent a window, nothing is marked and
         every label is byte-identical to the pre-4310 one. */
      label: formatBucketLabel(p?.bucket, granularity) + (isPartial ? PARTIAL_BUCKET_MARKER : ''),
      isPartial,
      totalMembers: running,
      grossRevenue: toFiniteOrNull(fo.grossRevenue),
      netRevenue: toFiniteOrNull(fo.netRevenue),
      totalExpense: toFiniteOrNull(fo.totalExpense),
      profit: toFiniteOrNull(fo.profit),
      reserveWithheld: convertCentsToDollars(
        deriveReserveWithheldCents(fo.grossRevenueCents, fo.netRevenueCents)
      )
    };
  });
};

/**
 * Pure. What the drawn window could NOT resolve — the chip criterion 4 requires. Two different
 * unknowns, both taken from the payload and deliberately kept apart:
 *   `unresolvedLines`        — a stored economics row whose profit is null
 *   `ordersWithoutEconomics` — an order carrying no stored economics row at all
 * Summed over the buckets ACTUALLY DRAWN, so the chip describes this chart rather than a window
 * the chart is not showing. The money view counts the first-order population's unresolved lines,
 * matching the series beside it.
 */
export const deriveUnresolved = (points = [], view = DEFAULT_EXECUTIVE_VIEW) => {
  const rows = Array.isArray(points) ? points : [];
  const sum = (pick) => rows.reduce((total, p) => total + (toFiniteOrNull(pick(p)) ?? 0), 0);
  const unresolvedLines = view === 'money'
    ? sum((p) => p?.firstOrder?.unresolvedLines)
    : sum((p) => p?.unresolvedLines);
  const ordersWithoutEconomics = sum((p) => p?.ordersWithoutEconomics);
  return Object.freeze({
    unresolvedLines,
    ordersWithoutEconomics,
    total: unresolvedLines + ordersWithoutEconomics
  });
};

/**
 * Pure. The caption's population name for a view, taken from the SERVER's `population` block.
 * ⛔ No population name is spelled anywhere in `web/` — that is what makes the money view's
 * label grep-assertable as appearing exactly once in the tree, in the API's constant. Returns
 * `null` when the server published none, so the caption renders nothing rather than a claim the
 * payload never made. (`population.members` ships with card 3870.)
 */
export const resolvePopulationLabel = (population, view) =>
  (view === 'members'
    ? population?.members?.label ?? null
    : population?.firstOrder?.label ?? null);

/**
 * Pure. The members view's headline figures, resolved from whichever payload generation is in
 * hand. Card 3870 tightens `newMembers` and adds `totalMembersGenuine`, `newMembersExcluded`
 * (an OBJECT of five drop reasons) and a PER-BUCKET `memberGrowthPct`; before it lands the
 * endpoint publishes only `totalMembers` and a window-level `newMembersGrowthPct`. The `??`
 * chain is the house dual-source idiom and means this view is correct on BOTH payloads instead
 * of blank on one of them.
 *
 * 🪤 PRESENCE, NOT TRUTHINESS, SELECTS THE GROWTH SOURCE. `memberGrowthPct` is legitimately
 *   `null` when a bucket has no baseline, so `??`-ing it would silently swap in
 *   `newMembersGrowthPct` — a DIFFERENT metric over a different comparison — exactly where the
 *   honest answer is "no baseline". `hasOwnProperty` picks the source; `null` then stays `null`.
 *
 * 🪤 BOTH FIELDS ARE ALREADY PERCENTS (⛔ not fractions like `profitMargin`, LEARNINGS:5374), so
 *   neither is rescaled here and both go through the same `formatGrowthPct` the KPI row uses.
 */
/**
 * Pure. The API's denominator TOKEN -> the words rendered beside the margin figure.
 * `'netRevenue'` -> `'net revenue'`.
 *
 * ⛔ A LOOKUP TABLE HERE WOULD BE THE CLIENT'S SECOND OPINION ABOUT THE SERVER'S OWN FIELD.
 *   Splitting the payload's camelCase key is mechanical, so if #4313 ever divides by something
 *   else the words on screen change with it instead of quietly naming the old denominator.
 *   `null` for a missing or non-string token — the caller then renders nothing rather than a
 *   sentence with an empty divisor in it.
 */
export const formatDenominatorLabel = (token) => {
  const raw = typeof token === 'string' ? token.trim() : '';
  if (!raw) return null;
  return raw.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
};

/**
 * Pure. The revenue-integrity block — #4313 publishes it, #4314 renders it. `null` when the
 * payload carries none, so an older API generation renders NOTHING here.
 *
 * 🔑 "NO DISCLOSURE" AND "NOTHING TO DISCLOSE" ARE DIFFERENT CLAIMS, so they get different
 *   values. A payload without the block returns `null` (say nothing); a payload whose count is
 *   genuinely 0 returns a block with `hasZeroRevenueOrders: false` (there is nothing to say).
 *   Collapsing the two would let a stale API look like a clean month.
 *
 * 🪤 `hasZeroRevenueOrders` IS A COUNT TEST, ⛔ NOT A TRUTHINESS TEST, and it is the chip's
 *   only render gate. A `0 zero-revenue` chip asserts to the reader that outliers were looked
 *   for and found, which on a clean period is simply false.
 *
 * ⛔ NOTHING HERE COMPUTES MONEY. Every figure is `aggregateEconomics` output read back; the
 *   margin-excluding-outliers is the SERVER's `profitMarginExZeroRevenue`, never a second
 *   division performed in the client.
 */
export const deriveRevenueIntegrity = (metrics = null) => {
  const block = metrics?.revenueIntegrity;
  if (!block) return null;
  const zeroRevenueOrderCount = toFiniteOrNull(block.zeroRevenueOrderCount) ?? 0;
  const denominator = typeof block.marginDenominator === 'string' ? block.marginDenominator : null;
  return Object.freeze({
    zeroRevenueOrderCount,
    hasZeroRevenueOrders: zeroRevenueOrderCount > 0,
    zeroRevenueExpense: toFiniteOrNull(block.zeroRevenueExpense),
    estimatedRevenueOrderCount: toFiniteOrNull(block.estimatedRevenueOrderCount) ?? 0,
    estimatedRevenue: toFiniteOrNull(block.estimatedRevenue),
    unresolvedZeroRevenueOrderCount: toFiniteOrNull(block.unresolvedZeroRevenueOrderCount) ?? 0,
    excludedFromProfitability: block.excludedFromProfitability === true,
    reserveInformationalOnly: block.reserveInformationalOnly === true,
    reserveInformational: toFiniteOrNull(block.reserveInformational),
    profitMarginExZeroRevenue: toFiniteOrNull(block.profitMarginExZeroRevenue),
    denominator,
    denominatorLabel: formatDenominatorLabel(denominator)
  });
};

export const deriveMembersSummary = (metrics = null, points = []) => {
  const rows = Array.isArray(points) ? points : [];
  const last = rows.length ? rows[rows.length - 1] : null;
  const hasBucketGrowth = !!last && Object.prototype.hasOwnProperty.call(last, 'memberGrowthPct');
  const excluded = metrics?.newMembersExcluded;
  return Object.freeze({
    totalMembers:
      toFiniteOrNull(metrics?.totalMembersGenuine) ?? toFiniteOrNull(metrics?.totalMembers),
    newMembers: toFiniteOrNull(metrics?.newMembers),
    growthPct: hasBucketGrowth
      ? toFiniteOrNull(last.memberGrowthPct)
      : toFiniteOrNull(metrics?.newMembersGrowthPct),
    excludedTotal: excluded
      ? Object.values(excluded).reduce((s, v) => s + (toFiniteOrNull(v) ?? 0), 0)
      : null
  });
};

/**
 * Pure. Whether an executive panel may present its figures as SETTLED (`'ready'`) or must hold
 * its box and show a placeholder instead (`'skeleton'`).
 *
 * It takes the WHOLE panel state so the KPI row and the charts ask one question of one function
 * and cannot drift into two different ideas of "in flight". Only `loading` may decide the answer,
 * and both halves of that are load-bearing:
 *
 *   - `{ loading: true, metrics: <object> }` is STILL `'skeleton'`. `AdminDashboard` replaces
 *     `executiveMetrics` only on `response.success`, so the PREVIOUS period's figures are still
 *     in state while the next request is in flight. Rendering them would put a settled, confident
 *     number underneath a period label the reader has already moved away from.
 *
 *   - `{ loading: false, metrics: null }` is `'ready'`, NEVER `'skeleton'`. A failed fetch lands
 *     in exactly that state (metrics never set, loading back to false), and a panel that shimmers
 *     forever is an invisible failure. `'ready'` hands the decision back to the absent-value
 *     paths, which say the figure is not there rather than implying it is still coming.
 *
 * So absent and in-flight stay DIFFERENT facts, one level up from the way `toFiniteOrNull` keeps
 * absent and zero apart.
 */
export const resolveExecutivePanelState = (panel) =>
  (panel?.loading === true ? 'skeleton' : 'ready');
